import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Store as ReduxStore } from "redux";

import { OrderModules, OrderReduxAction, StoreModules } from "../index";
import { OrderUtils } from "../modules/Order";
import { orderActions } from "../redux_store/order/order.slice";
import { RootState } from "../redux_store/rootReducer";

const CHECK_TIME_DEBOUNCE_DELAY = 200;
const UPDATE_ORDER_RETRIES_COUNT = 2;

export enum TimeShiftedType {
  Default,
  Menu,
  BreakfastMenu,
  LunchMenu,
}

const breakfastTitle = "Breakfast";

interface UseVerifyTimeParameters {
  onTimeShiftedCallback: (type: TimeShiftedType) => void;
  onTimeUpdatedCallback: (
    time: number | null,
    shouldUpdateOrder: boolean
  ) => void;
  calculatedOrderTime: number | null;
  reduxStore?: ReduxStore | null;
}

const useVerifyTime = ({
  onTimeShiftedCallback,
  onTimeUpdatedCallback,
  calculatedOrderTime,
  reduxStore,
}: UseVerifyTimeParameters) => {
  const dispatch = useDispatch();
  const counterRef = useRef<ReturnType<typeof setInterval>>();
  const {
    orderASAP,
    orderInitialTime,
    orderTime,
    getOrderResponse,
    getOrderLoading,
  } = useSelector((s: RootState) => s.order);
  const { selectedStore, storeOrderOffset, storeOrderTimes } = useSelector(
    (s: RootState) => s.store
  );
  const { totalPrice: cartTotalPrice } = useSelector(
    (state: RootState) => state.cart
  );
  const { menuStructure } = useSelector((s: RootState) => s.menu);
  const prevCartTotalPrice = useRef(cartTotalPrice);
  const updateOrderRetriesLeft = useRef(UPDATE_ORDER_RETRIES_COUNT);

  const getTimeShiftedType = useCallback(() => {
    const menuOrderedFrom = OrderUtils.getEffectiveMenuForOrderTime(
      orderInitialTime,
      menuStructure?.sections,
      menuStructure?.store?.timeZoneInfo?.storeTimeZone
    );
    if (menuOrderedFrom.length > 0) {
      const menu = menuOrderedFrom[0];
      if (menu.title == breakfastTitle) {
        return TimeShiftedType.BreakfastMenu;
      } else {
        return TimeShiftedType.LunchMenu;
      }
    }
    return TimeShiftedType.Default;
  }, [
    menuStructure?.sections,
    menuStructure?.store?.timeZoneInfo?.storeTimeZone,
    orderInitialTime,
  ]);

  const verifyTime = useMemo(
    () =>
      debounce(
        (
          _orderInitialTime: number | null,
          _calculatedOrderTime: number | null
        ) => {
          if (_orderInitialTime === _calculatedOrderTime) {
            return;
          }
          // has an order shifted to another trading hour period range? To display a popup for when
          // order shifts between breakfast to lunch and vice versa, making the current order likely to be invalid
          const isOrderShiftedToNextPeriod = menuStructure
            ? OrderUtils.hasOrderShiftedToNextPeriod(
                menuStructure.sections,
                _orderInitialTime,
                _calculatedOrderTime,
                menuStructure.store.timeZoneInfo.storeTimeZone
              )
            : false;
          if (
            OrderUtils.hasOrderTimeShifted(
              dispatch,
              _orderInitialTime,
              _calculatedOrderTime
            ) ||
            isOrderShiftedToNextPeriod
          ) {
            if (getOrderResponse) {
              onTimeShiftedCallback(
                isOrderShiftedToNextPeriod
                  ? getTimeShiftedType()
                  : TimeShiftedType.Default
              );
              dispatch(
                orderActions.updateOrderInitialTime(_calculatedOrderTime)
              );
            }
          }
        },
        CHECK_TIME_DEBOUNCE_DELAY
      ),
    [
      dispatch,
      getOrderResponse,
      getTimeShiftedType,
      menuStructure,
      onTimeShiftedCallback,
    ]
  );

  useEffect(() => {
    verifyTime(orderInitialTime, calculatedOrderTime);
  }, [orderInitialTime, calculatedOrderTime, verifyTime]);

  const checkIfOrderPickupTimeMeetRangesAndUpdateOrderIfNeeded = useCallback(
    (time: number | null) => {
      if (time && getOrderResponse && !getOrderLoading) {
        const isTimeMeetPickupTimeRanges =
          StoreModules.StoreUtils.isTimeMeetPickupTimeRanges(
            time,
            getOrderResponse
          );
        if (!isTimeMeetPickupTimeRanges && updateOrderRetriesLeft.current > 0) {
          updateOrderRetriesLeft.current = updateOrderRetriesLeft.current - 1;
          onTimeUpdatedCallback(null, true);
        } else if (
          !isTimeMeetPickupTimeRanges &&
          updateOrderRetriesLeft.current == 0
        ) {
          //if pickup time still doesn't meet pickup time ranges after retries update the time to the first one from pickupTimeOffsets
          const nextAvailASAPTime =
            OrderModules.OrderUtils.getFirstAvailableTimeFromPickupTimeOffsets(
              getOrderResponse
            );
          onTimeUpdatedCallback(nextAvailASAPTime, false);
          dispatch(OrderReduxAction.updateOrderTime(nextAvailASAPTime));
        } else {
          updateOrderRetriesLeft.current = UPDATE_ORDER_RETRIES_COUNT;
        }
      }
    },
    [dispatch, getOrderLoading, getOrderResponse, onTimeUpdatedCallback]
  );

  const updateCalculatedAsapTime = useCallback(() => {
    //Do not refresh time while order is loading
    if (getOrderLoading) {
      return;
    }
    const isCartTotalChanged = prevCartTotalPrice.current !== cartTotalPrice;
    const hasTimeslotsAndOffset =
      storeOrderOffset.length > 0 && storeOrderTimes.length > 0;
    // only calculate the next available time if there are offset and timeslots for store
    // as it will cause an infinite re-render when returned time is the new current time

    if (hasTimeslotsAndOffset) {
      // this is the earliest possible time
      const predictedAsapTime =
        StoreModules.StoreUtils.calculateTimeFromOffset(
          getOrderResponse
        ).valueOf();
      const nextAvailASAPTime = OrderModules.OrderUtils.getNextAvailableTime(
        reduxStore,
        predictedAsapTime,
        storeOrderTimes,
        selectedStore?.timeZoneInfo.storeTimeZone,
        getOrderResponse
      );

      //check if order time is ASAP or scheduled order time is earlier than fastest possible time
      if (orderASAP || orderTime < nextAvailASAPTime) {
        checkIfOrderPickupTimeMeetRangesAndUpdateOrderIfNeeded(
          nextAvailASAPTime
        );
        onTimeUpdatedCallback(nextAvailASAPTime, false);
        dispatch(OrderReduxAction.updateOrderTime(nextAvailASAPTime));
      } else {
        onTimeUpdatedCallback(orderTime, false);
      }
      // in the scenario where no offset and timeslots are available and cart
      // has changed
    } else if (!hasTimeslotsAndOffset && isCartTotalChanged && orderASAP) {
      const nowTime = StoreModules.StoreUtils.getOrderNowTime().valueOf();
      prevCartTotalPrice.current = cartTotalPrice;
      dispatch(OrderReduxAction.updateOrderTime(nowTime));
    }
  }, [
    cartTotalPrice,
    checkIfOrderPickupTimeMeetRangesAndUpdateOrderIfNeeded,
    dispatch,
    getOrderLoading,
    getOrderResponse,
    onTimeUpdatedCallback,
    orderASAP,
    orderTime,
    reduxStore,
    selectedStore?.timeZoneInfo?.storeTimeZone,
    storeOrderOffset?.length,
    storeOrderTimes,
  ]);

  // run timer to update asap time based on time passed
  useEffect(() => {
    if (selectedStore) {
      //clear previous interval when store changes
      if (counterRef.current) {
        clearInterval(counterRef.current);
      }
      counterRef.current = setInterval(() => {
        updateCalculatedAsapTime();
      }, StoreModules.StoreUtils.ORDER_TIME_TIMER_DELAY); //check if asap time changed

      return () => clearInterval(counterRef.current);
    }
  }, [selectedStore, updateCalculatedAsapTime]);

  const clearIntervalCallBack = useCallback(() => {
    return () => {
      clearInterval(counterRef.current);
    };
  }, []);

  useEffect(() => {
    if (getOrderResponse) {
      updateCalculatedAsapTime();
    }
  }, [getOrderResponse, updateCalculatedAsapTime]);

  return { updateCalculatedAsapTime, clearIntervalCallBack };
};

export default useVerifyTime;
